import { useState, useCallback } from "react";
import {
  submitRequest,
  approveRequest,
  rejectRequest,
  markAsPrinted,
  markAsDelivered,
  updateRequest,
} from "@/data/credentialReqRepository";
import { validateCredentialRequestCode } from "../utils/codeValidation";
import type { CredentialRequest } from "../types";

interface UseRequestActionsProps {
  requestId?: string;
  userId?: string;
  ministerCount: number;
  notes?: string;
  code?: string;
  requestYear?: number;
  isAdmin?: boolean;
  onRequestUpdate: (request: CredentialRequest) => void;
  onCodeValidationError?: (error: string) => void;
  onSuccess?: (action: 'submit' | 'approve' | 'reject' | 'print' | 'deliver') => void;
}

interface UseRequestActionsReturn {
  isSubmitting: boolean;
  error: string | null;
  
  handleSubmit: () => Promise<void>;
  handleApprove: () => Promise<void>;
  handleReject: (notes: string) => Promise<void>;
  handleMarkPrinted: () => Promise<void>;
  handleMarkDelivered: () => Promise<void>;
  setError: (error: string | null) => void;
}

/**
 * Hook to manage credential request workflow actions
 */
export function useRequestActions({
  requestId,
  userId,
  ministerCount,
  notes,
  code,
  requestYear,
  isAdmin,
  onRequestUpdate,
  onCodeValidationError,
  onSuccess,
}: UseRequestActionsProps): UseRequestActionsReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!requestId) return;

    if (ministerCount === 0) {
      setError("Debe agregar al menos un ministro antes de enviar");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const updates: any = {};
      
      // Update notes if they've changed
      if (notes !== undefined) {
        updates.notes = notes || null;
      }
      
      // Validate and update code if admin changed it
      if (isAdmin && code !== undefined && code.trim() && requestYear) {
        const validation = validateCredentialRequestCode(code, requestYear);
        if (!validation.isValid) {
          if (onCodeValidationError) {
            onCodeValidationError(validation.error || "Código inválido");
          }
          setIsSubmitting(false);
          return;
        }
        updates.code = validation.formattedCode;
      }
      
      // Save updates if any
      if (Object.keys(updates).length > 0) {
        await updateRequest(requestId, updates);
      }
      
      const updated = await submitRequest(requestId);
      if (updated) {
        onRequestUpdate(updated);
        if (onSuccess) {
          onSuccess('submit');
          // Keep loading state for navigation delay (500ms + 100ms buffer)
          setTimeout(() => setIsSubmitting(false), 600);
        } else {
          setIsSubmitting(false);
        }
      } else {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error submitting request:", err);
      setError(err instanceof Error ? err.message : "Error al enviar solicitud");
      setIsSubmitting(false);
    }
  }, [requestId, ministerCount, notes, code, requestYear, isAdmin, onRequestUpdate, onCodeValidationError]);

  const handleApprove = useCallback(async () => {
    if (!requestId || !userId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updated = await approveRequest(requestId, userId);
      if (updated) {
        onRequestUpdate(updated);
        if (onSuccess) {
          onSuccess('approve');
          setTimeout(() => setIsSubmitting(false), 600);
        } else {
          setIsSubmitting(false);
        }
      } else {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error approving request:", err);
      setError(err instanceof Error ? err.message : "Error al aprobar solicitud");
      setIsSubmitting(false);
    }
  }, [requestId, userId, onRequestUpdate]);

  const handleReject = useCallback(async (observations: string) => {
    if (!requestId || !userId) return;

    if (!observations.trim()) {
      setError("Las observaciones son requeridas");
      return;
    }

    if (observations.length > 500) {
      setError("Las observaciones no pueden exceder 500 caracteres");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const updated = await rejectRequest(requestId, userId, observations);
      if (updated) {
        onRequestUpdate(updated);
        if (onSuccess) {
          onSuccess('reject');
          setTimeout(() => setIsSubmitting(false), 600);
        } else {
          setIsSubmitting(false);
        }
      } else {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error rejecting request:", err);
      setError(err instanceof Error ? err.message : "Error al observar solicitud");
      setIsSubmitting(false);
    }
  }, [requestId, userId, onRequestUpdate]);

  const handleMarkPrinted = useCallback(async () => {
    if (!requestId || !userId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updated = await markAsPrinted(requestId, userId);
      if (updated) {
        onRequestUpdate(updated);
        if (onSuccess) {
          onSuccess('print');
          setTimeout(() => setIsSubmitting(false), 600);
        } else {
          setIsSubmitting(false);
        }
      } else {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error marking as printed:", err);
      setError(err instanceof Error ? err.message : "Error al marcar como impreso");
      setIsSubmitting(false);
    }
  }, [requestId, userId, onRequestUpdate]);

  const handleMarkDelivered = useCallback(async () => {
    if (!requestId || !userId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updated = await markAsDelivered(requestId, userId);
      if (updated) {
        onRequestUpdate(updated);
        if (onSuccess) {
          onSuccess('deliver');
          setTimeout(() => setIsSubmitting(false), 600);
        } else {
          setIsSubmitting(false);
        }
      } else {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error marking as delivered:", err);
      setError(err instanceof Error ? err.message : "Error al marcar como entregado");
      setIsSubmitting(false);
    }
  }, [requestId, userId, onRequestUpdate]);

  return {
    isSubmitting,
    error,
    handleSubmit,
    handleApprove,
    handleReject,
    handleMarkPrinted,
    handleMarkDelivered,
    setError,
  };
}
